import * as EVENTS from './../../common/constant';
import * as CONSTANT from './../base/constant';
import { createElement, isNullOrUndefined } from '@syncfusion/ej2-base';
import { SelectionCommands } from './selection-commands';
import { DOMMethods } from './dom-tree';
/**
 * Auto Format internal component
 *
 * @hidden
 * @private
 */
export class AutoFormatPlugin {
    /**
     * Constructor for creating the Auto Format plugin
     *
     * @param {IEditorModel} parent - specifies the parent element.
     * @returns {void}
     * @hidden
     * @private
     */
    constructor(parent) {
        this.parent = parent;
        this.addEventListener();
    }
    addEventListener() {
        this.parent.observer.on(EVENTS.AUTO_FORMAT_ACTIONS, this.autoFormat, this);
        this.parent.observer.on(EVENTS.INTERNAL_DESTROY, this.destroy, this);
    }
    removeEventListener() {
        this.parent.observer.off(EVENTS.AUTO_FORMAT_ACTIONS, this.autoFormat);
        this.parent.observer.off(EVENTS.INTERNAL_DESTROY, this.destroy);
    }
    findAutoFormatCommandInRange(range, text = null) {
        const commandsArray = ['*', '~', '`', '_'];
        // Early return if range is null/undefined, use text-based check
        if (!range) {
            return commandsArray.some((command) => text.includes(command));
        }
        const startContainer = range.startContainer;
        // Early return if not a text node
        if (startContainer.nodeType !== Node.TEXT_NODE) {
            return false;
        }
        const textContent = startContainer.textContent || '';
        const cursorPos = range.startOffset;
        // Early return if no text before cursor
        if (cursorPos <= 0) {
            return false;
        }
        const textBeforeCursor = textContent.substring(0, cursorPos); // converting without space
        // Use some() to exit early on first match
        return commandsArray.some((command) => textBeforeCursor.endsWith(command));
    }
    findBlockAutoFormatCommandInRange(range, text = null) {
        const editorValue = range.startContainer.textContent.slice(0, range.startOffset - 1);
        const result = this.isValidBlockAutoFormatCommand(editorValue, range);
        return result;
    }
    autoFormat(e) {
        const eventArgs = e.event;
        eventArgs.preventDefault();
        const range = this.parent.nodeSelection.getRange(this.parent.currentDocument);
        const isCollapsed = range && range.startContainer === range.endContainer && range.startOffset === range.endOffset;
        if (!range || !isCollapsed) {
            return; // Ensure cursor is in a valid, collapsed range
        }
        const startContainer = range.startContainer;
        const cursorPos = range.startOffset;
        // Find the nearest parent element
        const firstBlockParent = this.findFirstBlockParent(startContainer);
        if (!firstBlockParent) {
            return; // No valid inline parent found
        }
        if (firstBlockParent.nodeName === 'PRE' && !(isNullOrUndefined(firstBlockParent.getAttribute('data-language')))) {
            return; // Not processing the inline auto formats for code block elements
        }
        // Get text and nodes for the current line
        const textAndOffset = this.getLineTextAndNodes(firstBlockParent, startContainer, cursorPos);
        const text = textAndOffset.text;
        if (!text) {
            return; // No text to process
        }
        // Find Markdown pattern match
        const matchInfo = this.findMarkdownMatch(text.slice(0, text.length)); // converting without space
        if (!matchInfo) {
            return; // No Markdown pattern matched
        }
        const matchedRange = this.findMarkdownStartEndNodes(textAndOffset.nodes, matchInfo);
        if (!matchedRange) {
            return;
        }
        this.processMarkdownMatch(matchInfo, textAndOffset.nodes, matchedRange);
        this.callBack(e, e.subCommand);
    }
    callBack(event, action) {
        if (event.callBack) {
            event.callBack({
                requestType: action,
                event: event.event,
                editorMode: 'HTML',
                isKeyboardEvent: event.name === EVENTS.KEY_DOWN_HANDLER ? true : false,
                range: this.parent.nodeSelection.getRange(this.parent.currentDocument),
                elements: this.parent.nodeSelection.getSelectedNodes(this.parent.currentDocument)
            });
        }
    }
    findFirstBlockParent(startContainer) {
        let node = startContainer;
        while (node && CONSTANT.BLOCK_TAGS.indexOf(node.nodeName.toLocaleLowerCase()) < 0) {
            node = node.parentNode;
        }
        return node;
    }
    getLineTextAndNodes(firstBlockParent, startContainer, cursorPos) {
        const text = '';
        const adjustedCursorPos = cursorPos;
        const nodes = [];
        const foundTarget = false;
        const result = this.traverseNodes(firstBlockParent, startContainer, cursorPos, text, adjustedCursorPos, nodes, foundTarget, firstBlockParent);
        return { text: result.text, adjustedCursorPos: result.adjustedCursorPos, nodes: result.nodes };
    }
    traverseNodes(firstBlockParent, startContainer, cursorPos, text, adjustedCursorPos, nodes, foundTarget, currentParent) {
        const nodeName = firstBlockParent.nodeName;
        if (nodeName === 'BR') {
            if (!foundTarget) {
                nodes = [];
                text = '';
                adjustedCursorPos = 0;
            }
        }
        if (firstBlockParent.nodeType === Node.TEXT_NODE) {
            const nodeText = firstBlockParent.textContent || '';
            nodes.push(firstBlockParent);
            if (firstBlockParent === startContainer) {
                text += nodeText.slice(0, cursorPos);
                foundTarget = true;
                return { text, adjustedCursorPos, nodes, foundTarget }; // Stop at cursor position
            }
            else {
                text += nodeText;
                adjustedCursorPos += nodeText.length;
            }
        }
        else if (firstBlockParent.nodeType === Node.ELEMENT_NODE) {
            const childNodes = firstBlockParent.childNodes;
            for (let i = 0; i < childNodes.length; i++) {
                const childResult = this.traverseNodes(childNodes[i], startContainer, cursorPos, text, adjustedCursorPos, nodes, foundTarget, currentParent);
                text = childResult.text;
                adjustedCursorPos = childResult.adjustedCursorPos;
                nodes = childResult.nodes;
                foundTarget = childResult.foundTarget;
                if (foundTarget) {
                    return { text, adjustedCursorPos, nodes, foundTarget }; // Stop if target found or no progress
                }
            }
        }
        return { text, adjustedCursorPos, nodes, foundTarget };
    }
    findMarkdownMatch(text) {
        // Define Markdown patterns/
        const patterns = [
            { regex: /\*\*\*([^*]+)\*\*\*$/, tag: 'em-strong', syntax: '***' },
            { regex: /___([^_]+)___$/, tag: 'em-strong', syntax: '___' },
            { regex: /\*\*([^*]+)\*\*$/, tag: 'strong', syntax: '**' },
            { regex: /__([^_]+)__$/, tag: 'strong', syntax: '__' },
            { regex: /\*([^*]+)\*$/, tag: 'em', syntax: '*' },
            { regex: /_([^_]+)_$/, tag: 'em', syntax: '_' },
            { regex: /`([^`]+)`$/, tag: 'code', syntax: '`' },
            { regex: /~~([^~]+)~~$/, tag: 's', syntax: '~~' } // ~~strikethrough~~
        ];
        const notAllowedMatches = [
            { regex: /\*+[^*]+\*+$/ },
            { regex: /_+[^_]+_+$/ }
        ];
        for (let i = 0; i < patterns.length; i++) {
            const pattern = patterns[i];
            const match = text.match(pattern.regex);
            if (match && match[1]) {
                if (pattern.tag === 'strong' || pattern.tag === 'em') {
                    for (let j = 0; j < notAllowedMatches.length; j++) {
                        const notAllowedMatch = notAllowedMatches[j];
                        const notAllowedMatchResult = text.match(notAllowedMatch.regex);
                        if (notAllowedMatchResult && notAllowedMatchResult[0] === match[0]) {
                            return { match, tag: pattern.tag, syntax: pattern.syntax };
                        }
                    }
                }
                else {
                    return { match: match, tag: pattern.tag, syntax: pattern.syntax };
                }
            }
        }
        return null;
    }
    isValidBlockAutoFormatCommand(editorValue, range) {
        const patterns = [
            { regex: /^#$/, tag: 'h1', format: 'Formats' },
            { regex: /^##$/, tag: 'h2', format: 'Formats' },
            { regex: /^###$/, tag: 'h3', format: 'Formats' },
            { regex: /^####$/, tag: 'h4', format: 'Formats' },
            { regex: /^#####$/, tag: 'h5', format: 'Formats' },
            { regex: /^######$/, tag: 'h6', format: 'Formats' },
            { regex: /^>$/, tag: 'blockquote', format: 'Formats' },
            { regex: /^(---|___)$/, tag: 'hr', format: 'InsertHtml' },
            { regex: /^```$/, tag: 'CodeBlock', format: 'CodeBlock' }
        ];
        const domMethods = new DOMMethods(this.parent.editableElement);
        const parentElement = (CONSTANT.BLOCK_TAGS.indexOf(range.startContainer.nodeName.toLocaleLowerCase()) > -1) ?
            range.startContainer : domMethods.getParentBlockNode(range.startContainer);
        const headingTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
        const notAllowedInCodeBlock = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'CodeBlock'];
        for (let i = 0; i < patterns.length; i++) {
            const pattern = patterns[i];
            const match = editorValue.match(pattern.regex);
            if (match && (pattern.tag !== parentElement.tagName.toLowerCase() || pattern.tag === 'blockquote')
                && !(parentElement.tagName === 'PRE' && !isNullOrUndefined(parentElement.getAttribute('data-language')) && notAllowedInCodeBlock.indexOf(pattern.tag) > -1)) {
                if (pattern.tag === 'CodeBlock' && isNullOrUndefined(this.parent.codeBlockObj)) {
                    return null;
                }
                // Remove the text content
                range.startContainer.textContent = range.startContainer.textContent.slice(range.startOffset, range.startContainer.textContent.length);
                // If the text node is now empty, replace it with a <br> element
                const emptyTextNode = range.startContainer;
                if (emptyTextNode.nodeType === Node.TEXT_NODE && emptyTextNode.textContent === '') {
                    const brElement = createElement('br');
                    const parentNode = emptyTextNode.parentNode;
                    if (parentNode) {
                        parentNode.replaceChild(brElement, emptyTextNode);
                    }
                }
                return { tag: pattern.tag, format: pattern.format };
            }
            else if (match && headingTags.indexOf(pattern.tag) > -1 && pattern.tag === parentElement.tagName.toLowerCase()) {
                range.startContainer.textContent = range.startContainer.textContent.slice(range.startOffset, range.startContainer.textContent.length);
                this.parent.nodeSelection.setCursorPoint(this.parent.currentDocument, parentElement, 0);
                return { tag: 'p', format: pattern.format };
            }
        }
        return null;
    }
    findMarkdownStartEndNodes(textNodes, matchInfo) {
        const match = matchInfo.match;
        const matchedText = match[0];
        const startIndex = match.index;
        const deleteLength = (matchedText.length - match[1].length) / 2;
        const syntax = matchInfo.syntax.slice(0, deleteLength > 1 ? deleteLength - 1 : 0);
        const endIndex = (startIndex + matchedText.length) - deleteLength;
        let cumulativeLength = 0;
        let startNodeIndex = -1;
        let endNodeIndex = -1;
        let startPosition = -1;
        let endPosition = -1;
        for (let i = 0; i < textNodes.length; i++) {
            const nodeText = textNodes[i].textContent || '';
            let nextCumulativeLength = cumulativeLength + nodeText.length;
            // Check if start index falls within this node
            if (startNodeIndex === -1 && startIndex >= cumulativeLength && startIndex < nextCumulativeLength) {
                startNodeIndex = i;
                startPosition = startIndex - cumulativeLength;
                nextCumulativeLength = nextCumulativeLength + this.deleteMarkerFromNodes(textNodes, i, startPosition, deleteLength, syntax);
            }
            // Check if end index falls within this nodes
            if (endIndex >= cumulativeLength && endIndex < nextCumulativeLength) {
                endNodeIndex = i;
                endPosition = endIndex - cumulativeLength;
                if (startNodeIndex === endNodeIndex) {
                    endPosition = endPosition - deleteLength;
                }
                nextCumulativeLength = nextCumulativeLength + this.deleteMarkerFromNodes(textNodes, i, endPosition, deleteLength, syntax);
                break; // End index found, no need to continue
            }
            cumulativeLength = nextCumulativeLength;
        }
        if (startNodeIndex === -1 || endNodeIndex === -1 || startPosition === -1 || endPosition === -1) {
            return null; // Match spans nodes incorrectly or not found
        }
        return { startNodeIndex, endNodeIndex, startPosition, endPosition };
    }
    deleteMarkerFromNodes(textNodes, currentIndex, startPosition, deleteLength, syntax) {
        const currentNode = textNodes[currentIndex];
        const currentText = currentNode.textContent || '';
        // Ensure startPosition is within bounds
        if (startPosition < 0 || startPosition >= currentText.length) {
            return 0;
        }
        // Calculate portion to delete from current node
        const deleteFromCurrent = Math.min(deleteLength, currentText.length - startPosition);
        if (deleteFromCurrent > 0) {
            currentNode.deleteData(startPosition, deleteFromCurrent);
        }
        // Handle remaining length if marker spans to next node
        const remainingLength = deleteLength - deleteFromCurrent;
        if (remainingLength > 0 && currentIndex + 1 < textNodes.length) {
            const nextNode = textNodes[currentIndex + 1];
            const nextText = nextNode.textContent || '';
            if (nextText.startsWith(syntax.repeat(remainingLength))) {
                nextNode.deleteData(0, remainingLength);
            }
            return remainingLength;
        }
        return 0;
    }
    processMarkdownMatch(matchInfo, textNodes, macthedRange) {
        const tag = matchInfo.tag;
        switch (tag) {
            case 'em-strong':
                // Wrap content with <em><strong> tags
                this.wrapContentWithTags(textNodes, macthedRange, 'italic', 'bold');
                break;
            case 'strong':
                // Wrap content with <strong> tag
                this.wrapContentWithTags(textNodes, macthedRange, 'bold');
                break;
            case 'em':
                // Wrap content with <em> tag
                this.wrapContentWithTags(textNodes, macthedRange, 'italic');
                break;
            case 'code':
                // Wrap content with <code> tag
                this.wrapContentWithTags(textNodes, macthedRange, 'inlinecode');
                break;
            case 's':
                // Wrap content with <s> tag for strikethrough
                this.wrapContentWithTags(textNodes, macthedRange, 'strikethrough');
                break;
        }
    }
    wrapContentWithTags(textNodes, macthedRange, ...tags) {
        // Implementation to wrap content with specified tags
        // This is a placeholder; actual DOM manipulation would depend on your editor's API
        const range = document.createRange();
        let startNode = textNodes[macthedRange.startNodeIndex];
        const endNode = textNodes[macthedRange.endNodeIndex];
        if (startNode.textContent.length === macthedRange.startPosition) {
            startNode = textNodes[macthedRange.startNodeIndex + 1];
            macthedRange.startPosition = 0;
        }
        range.setStart(startNode, macthedRange.startPosition);
        range.setEnd(endNode, macthedRange.endPosition);
        this.parent.nodeSelection.setSelectionText(this.parent.currentDocument, range.startContainer, range.endContainer, range.startOffset, range.endOffset);
        SelectionCommands.applyFormat(this.parent.currentDocument, tags[0], this.parent.editableElement, this.enterkey);
        if (!isNullOrUndefined(tags[1])) {
            SelectionCommands.applyFormat(this.parent.currentDocument, tags[1], this.parent.editableElement, this.enterkey);
        }
        const selectedNodes = this.parent.nodeSelection.getSelectedNodes(this.parent.currentDocument);
        const selectedContent = selectedNodes[selectedNodes.length - 1];
        const isBoldItalic = tags.length === 2;
        const isRevert = this.isReverted(selectedContent, tags);
        this.setCursorPoint(selectedContent, isBoldItalic, isRevert);
    }
    setCursorPoint(selectedContent, isBoldItalic, isRevert) {
        const doc = this.parent.currentDocument;
        if (isRevert) {
            this.parent.nodeSelection.setCursorPoint(doc, selectedContent, selectedContent.textContent.length);
        }
        else if (!isBoldItalic) {
            selectedContent.parentElement.insertAdjacentText('afterend', '\u200B'); // added a zero width space and focused
            this.parent.nodeSelection.setCursorPoint(doc, selectedContent.parentElement.nextSibling, 1);
        }
        else {
            selectedContent.parentElement.parentElement.insertAdjacentText('afterend', '\u200B'); // added a zero width space and focused
            this.parent.nodeSelection.setCursorPoint(doc, selectedContent.parentElement.parentElement.nextSibling, 1);
        }
    }
    isReverted(selectedContent, tags) {
        const element = tags[tags.length - 1];
        const parentElement = selectedContent.parentElement;
        let appliedFormat;
        switch (element) {
            case 'bold':
                appliedFormat = 'STRONG';
                break;
            case 'italic':
                appliedFormat = 'EM';
                break;
            case 'inlinecode':
                appliedFormat = 'CODE';
                break;
            case 'strikethrough':
                appliedFormat = 'SPAN';
                break;
        }
        if (appliedFormat === 'SPAN') {
            if (parentElement.nodeName === 'SPAN' && parentElement.style.textDecoration === 'line-through') {
                return false;
            }
            else {
                return true;
            }
        }
        if (parentElement.nodeName === appliedFormat) {
            return false;
        }
        else {
            return true;
        }
    }
    destroy() {
        this.removeEventListener();
    }
}
